import { useState, useEffect } from "react";
import { ChevronDown, Filter, Star, DollarSign, Film, Music, Play, Bot, Palette, Scissors, Wand2, Tv, Video, Crown, Sparkles, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { fetchCategories, Category } from "@/lib/api";
import { cn } from "@/lib/utils";

const categoryIcons: Record<string, React.ReactNode> = {
  netflix: <Film className="h-4 w-4" />,
  spotify: <Music className="h-4 w-4" />,
  youtube: <Play className="h-4 w-4" />,
  chatgpt: <Bot className="h-4 w-4" />,
  canva: <Palette className="h-4 w-4" />,
  capcut: <Scissors className="h-4 w-4" />,
  disney: <Star className="h-4 w-4" />,
  gemini: <Wand2 className="h-4 w-4" />,
  hbo: <Tv className="h-4 w-4" />,
  prime: <Video className="h-4 w-4" />,
  viu: <Crown className="h-4 w-4" />,
};

const categoryGradients: Record<string, string> = {
  netflix: "from-[#E50914] to-[#B81D24]",
  spotify: "from-[#1DB954] to-[#158D3E]",
  youtube: "from-[#FF0000] to-[#CC0000]",
  chatgpt: "from-[#10A37F] to-[#0D8F6F]",
  canva: "from-[#7B2BFB] to-[#00C4CC]",
  capcut: "from-[#F72585] to-[#7209B7]",
  disney: "from-[#113CCF] to-[#0D2A8A]",
  gemini: "from-[#4285F4] to-[#8B5CF6]",
  hbo: "from-[#5822B4] to-[#3A1078]",
  prime: "from-[#00A8E1] to-[#146EB4]",
  viu: "from-[#FFB800] to-[#FF8C00]",
};

const subscriptionTypes = [
  { value: "all", label: "Semua" },
  { value: "private", label: "Private" },
  { value: "sharing_1p1u", label: "Sharing 1P1U" },
  { value: "sharing", label: "Sharing" },
  { value: "invite", label: "Invite" },
  { value: "family_head", label: "Family Head" },
  { value: "family_plan_invite", label: "Family Plan" },
  { value: "voucher", label: "Voucher" },
  { value: "individual", label: "Individual" },
];

export interface FilterState {
  category: string;
  status: string;
  type: string;
  minPrice: string;
  maxPrice: string;
  bestSellerOnly: boolean;
}

interface ProductFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

export function ProductFilters({ filters, onFilterChange }: ProductFiltersProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);

  useEffect(() => {
    async function loadCategories() {
      const data = await fetchCategories();
      setCategories(data);
    }
    loadCategories();
  }, []);

  const visibleCategories = showAllCategories ? categories : categories.slice(0, 6);
  const visibleTypes = showAllTypes ? subscriptionTypes : subscriptionTypes.slice(0, 5);

  const updateFilter = (key: keyof FilterState, value: string | boolean) => {
    onFilterChange({ ...filters, [key]: value });
  };

  return (
    <Card className="sticky top-20 border border-border bg-card/80 backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Filter className="h-5 w-5 text-primary" />
          Filter Produk
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Category Filter with Icons */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Kategori</Label>
          <div className="grid grid-cols-2 gap-2">
            {/* All Categories Button */}
            <button
              onClick={() => updateFilter("category", "all")}
              className={cn(
                "flex items-center gap-2 p-2.5 rounded-lg border transition-all duration-200 text-left",
                filters.category === "all"
                  ? "bg-primary text-primary-foreground border-primary shadow-lg"
                  : "bg-muted/50 border-border hover:border-primary/50 hover:bg-muted"
              )}
            >
              <div className={cn(
                "w-7 h-7 rounded-lg flex items-center justify-center",
                filters.category === "all"
                  ? "bg-white/20"
                  : "bg-gradient-to-br from-primary to-primary/80 text-white"
              )}>
                <LayoutGrid className="h-4 w-4" />
              </div>
              <span className="text-xs font-medium truncate">Semua</span>
            </button>

            {visibleCategories.map((cat) => {
              const icon = categoryIcons[cat.slug] || <Sparkles className="h-4 w-4" />;
              const gradient = categoryGradients[cat.slug] || "from-primary to-primary/80";
              const isSelected = filters.category === cat.slug;

              return (
                <button
                  key={cat.id}
                  onClick={() => updateFilter("category", cat.slug)}
                  className={cn(
                    "flex items-center gap-2 p-2.5 rounded-lg border transition-all duration-200 text-left",
                    isSelected
                      ? "bg-primary text-primary-foreground border-primary shadow-lg"
                      : "bg-muted/50 border-border hover:border-primary/50 hover:bg-muted"
                  )}
                >
                  <div className={cn(
                    "w-7 h-7 rounded-lg flex items-center justify-center text-white",
                    isSelected ? "bg-white/20" : `bg-gradient-to-br ${gradient}`
                  )}>
                    {icon}
                  </div>
                  <span className="text-xs font-medium truncate">{cat.name.replace(" Premium", "").replace(" Pro", "")}</span>
                </button>
              );
            })}
          </div>

          {categories.length > 6 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="text-primary text-xs p-0 h-auto w-full justify-center"
            >
              <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showAllCategories ? "rotate-180" : ""}`} />
              {showAllCategories ? "Tampilkan Sedikit" : `Lihat Semua (${categories.length})`}
            </Button>
          )}
        </div>

        {/* Best Seller Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
          <Label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
            Best Seller Only
          </Label>
          <Switch 
            checked={filters.bestSellerOnly} 
            onCheckedChange={(checked) => updateFilter("bestSellerOnly", checked)} 
          />
        </div>

        {/* Status Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Status</Label>
          <div className="flex gap-2">
            {[
              { value: "all", label: "Semua" },
              { value: "available", label: "Tersedia" },
              { value: "outOfStock", label: "Habis" },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => updateFilter("status", s.value)}
                className={cn(
                  "flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 border",
                  filters.status === s.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/50 border-border hover:border-primary/50"
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subscription Type Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Tipe Langganan</Label>
          <div className="flex flex-wrap gap-2">
            {visibleTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => updateFilter("type", type.value)}
                className={cn(
                  "py-1.5 px-3 rounded-full text-xs font-medium transition-all duration-200 border",
                  filters.type === type.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/50 border-border hover:border-primary/50"
                )}
              >
                {type.label}
              </button>
            ))}
          </div>
          {subscriptionTypes.length > 5 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllTypes(!showAllTypes)}
              className="text-primary text-xs p-0 h-auto"
            >
              <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showAllTypes ? "rotate-180" : ""}`} />
              {showAllTypes ? "Sedikit" : "Lainnya"}
            </Button>
          )}
        </div>

        {/* Price Range */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-sm font-medium">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            Rentang Harga
          </Label>
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={filters.minPrice}
              onChange={(e) => updateFilter("minPrice", e.target.value)}
              className="text-sm bg-muted/50"
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={(e) => updateFilter("maxPrice", e.target.value)}
              className="text-sm bg-muted/50"
            />
          </div>
        </div>

        {/* Reset Filters */}
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => onFilterChange({
            category: "all",
            status: "all",
            type: "all",
            minPrice: "",
            maxPrice: "",
            bestSellerOnly: false,
          })}
        >
          Reset Filter
        </Button>
      </CardContent>
    </Card>
  );
}